import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
import { Anchor } from '@twilio-paste/anchor';
import { Box } from '@twilio-paste/box';
import { Breadcrumb, BreadcrumbItem } from '../../../components/breadcrumb';
import { Callout, CalloutTitle, CalloutText } from '../../../components/callout';
import { Card } from '@twilio-paste/card';
import { Disclosure, DisclosureHeading, DisclosureContent } from '@twilio-paste/disclosure';
import { Grid, Column } from '@twilio-paste/grid';
import { Heading } from '@twilio-paste/heading';
import { Paragraph } from '@twilio-paste/paragraph';
import { Text } from '@twilio-paste/text';
import { useUID } from 'react-uid';
export const pageQuery = graphql`
  {
    mdx(fields: {slug: {eq: "/patterns/delete/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const PageAside = makeShortcode("PageAside");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <content>
      <Breadcrumb mdxType="Breadcrumb">
  <BreadcrumbItem to="/" mdxType="BreadcrumbItem">Home</BreadcrumbItem>
  <BreadcrumbItem to="/patterns" mdxType="BreadcrumbItem">Patterns</BreadcrumbItem>
      </Breadcrumb>
      <Heading as="h1" variant="heading10" mdxType="Heading">
  {props.pageContext.frontmatter.title}
      </Heading>
      <Paragraph mdxType="Paragraph">{props.pageContext.frontmatter.description}</Paragraph>
      <Box as="dl" display="flex" mdxType="Box">
  <Text as="dt" color="colorTextWeak" lineHeight="lineHeight30" mdxType="Text">
    <Box width="size10" mdxType="Box">Status</Box>
  </Text>
  <Text as="dd" mdxType="Text">alpha</Text>
      </Box>
      <Callout variant="warning" mdxType="Callout">
  <CalloutTitle mdxType="CalloutTitle">This is an ALPHA pattern, and we need your feedback!</CalloutTitle>
  <CalloutText mdxType="CalloutText">
    This pattern is still going through feedback cycles, so if you have feedback or ideas related to this pattern,
    please create a <Anchor href="https://github.com/twilio-labs/paste/discussions" mdxType="Anchor">GitHub Discussion</Anchor> and let
    us know.
  </CalloutText>
      </Callout>
    </content>
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Ingredients`}</h2>
        <Grid gutter="space30" marginBottom="space80" mdxType="Grid">
  <Column mdxType="Column">
    <Card mdxType="Card">
      <Heading as="h3" variant="heading40" marginBottom="space0" mdxType="Heading">
        <Anchor href="../components/button" mdxType="Anchor">Button</Anchor>
      </Heading>
    </Card>
  </Column>
  <Column mdxType="Column">
    <Card mdxType="Card">
      <Heading as="h3" variant="heading40" marginBottom="space0" mdxType="Heading">
        <Anchor href="../components/Modal" mdxType="Anchor">Modal</Anchor>
      </Heading>
    </Card>
  </Column>
  <Column mdxType="Column">
    <Card mdxType="Card">
      <Heading as="h3" variant="heading40" marginBottom="space0" mdxType="Heading">
        <Anchor href="../components/input" mdxType="Anchor">Input</Anchor>
      </Heading>
    </Card>
  </Column>
  <Column mdxType="Column">
    <Card mdxType="Card">
      <Heading as="h3" variant="heading40" marginBottom="space0" mdxType="Heading">
        <Anchor href="../components/toast" mdxType="Anchor">Toast</Anchor>
      </Heading>
    </Card>
  </Column>
        </Grid>
        <pre><code parentName="pre" {...{
            "className": "language-jsx"
          }}>{`// import all ingredients for the delete patterns

import {​
  Button,
  Modal, ModalBody, ModalFooter, ModalFooterActions, ModalHeader, ModalHeading,
  Input, Label, HelpText,
  Toast
} from '@twilio-paste/core
`}</code></pre>
        <h2>{`Usage`}</h2>
        <h3>{`General`}</h3>
        <p>{`The delete pattern should be utilized when a user is destroying an object. It is critical to communicate delete actions to our users in a consistent way, considering deletions come with significant consequences and often cannot be undone.`}</p>
        <p>{`The delete action should:`}</p>
        <ul>
          <li parentName="ul">{`Be triggered by a destructive Button.`}</li>
          <li parentName="ul">{`Generally be placed at the bottom of an `}<a parentName="li" {...{
              "href": "/patterns/object-details"
            }}>{`Object Details`}</a>{` page.`}</li>
          <li parentName="ul">{`Not usually be the primary action on the page. There are many variants of differing visual prominence to choose from to achieve the correct hierarchy for the delete action trigger.`}</li>
          <li parentName="ul">{`Follow certain guidelines based on the `}<a parentName="li" {...{
              "href": "#variations"
            }}>{`severity`}</a>{` of the deletion.`}</li>
        </ul>
        <h2>{`Variations`}</h2>
        <h3>{`Low-severity`}</h3>
        <p>{`A deletion is considered low-severity when it is trivial to undo the deletion or recreate the deleted data.`}</p>
        <p>{`Low-severity deletions can be triggered by a single click and do not require further warning or confirmation.`}</p>
        <h3>{`Medium-severity`}</h3>
        <p>{`A deletion is considered medium-severity when the action cannot be undone, and the data cannot be recreated easily. This pattern is also useful for bulk deletions of low- or medium-severity.`}</p>
        <p>{`For medium-severity deletions, show a confirmation modal that explains what is being deleted and the consequences of the deletion.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1632px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "53.67647058823529%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAAB5UlEQVQoz6WSb0/TUBTG73fSj2OMfyIb30HDG94g4Jzub7tu3dpurMpgYzKGQogJTF+IqG/0lVNnZO3aLcCkXbs+nlbcC0lU4k1+ec656XnuPfeUMXZ5hrFLL4lnxPZ/8JyosatXZqrhqUXcuDaL0K05hKfmMB26+4/MYzo8/zMOL+Dm9VmL8dxKWZGbEISqLUsNV5E3XDFXd3PZtb8iZGpE1c0KNYfqoMiNHotEiiqXruJBdMlJJh6D51axs/MKrdbbgL29NxP12d09mOT7++9x8PoDXrTeeWLuCZKJZZNFoyU1w68h9lB1Usll0O3w7VCDYZow+33ohoH+YBDE/p7e6wWqkf5almV5peImqP53wwqoFXQ6h9D0Hrpaj9QI8A/pdvUgNsw+hidDHB0dY+iO8d1xvaLSJMPKeUN6DwwGx8HJ4/E4wPO8APcs95dNYo8o/9TGyce2Vyo9PX/DNBkKZKhpJkYjB6enFrVjk9qBTqD9r6aNzhcDn2/fQTuW8IrlrTPD+6WldGqVDMtWOlVxaCgOte2IuT+TF33qTl5qOGJhfeQPk0uv6IymW1fkbfA0aanQhCxtQso3UbgI4gaKypb/h7hscUEKRe4pMsHFYuVMPK5mEvFHF8D/XuWpLhuPqdEfK2l05W79PqMAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "modal with a destrctive button to confirm deletion",
              "title": "modal with a destrctive button to confirm deletion",
              "src": "/static/c63ff72e084d691945d1e71ee6986301/1d499/delete-medium-severity.png",
              "srcSet": ["/static/c63ff72e084d691945d1e71ee6986301/e7c18/delete-medium-severity.png 408w", "/static/c63ff72e084d691945d1e71ee6986301/b4098/delete-medium-severity.png 816w", "/static/c63ff72e084d691945d1e71ee6986301/1d499/delete-medium-severity.png 1632w", "/static/c63ff72e084d691945d1e71ee6986301/2ef06/delete-medium-severity.png 2448w", "/static/c63ff72e084d691945d1e71ee6986301/d9ed5/delete-medium-severity.png 2880w"],
              "sizes": "(max-width: 1632px) 100vw, 1632px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <Box marginBottom="space80" mdxType="Box">
  <Disclosure variant="contained" mdxType="Disclosure">
    <DisclosureHeading as="h5" variant="heading50" mdxType="DisclosureHeading">
      Show live example
    </DisclosureHeading>
    <DisclosureContent mdxType="DisclosureContent">Coming soon!</DisclosureContent>
  </Disclosure>
        </Box>
        <h3>{`High-severity`}</h3>
        <p>{`A deletion is considered high-severity when the action cannot be undone, and it would be very time-consuming, or perhaps impossible, to recreate the deleted data. An action that deletes a large amount of data or has significant downstream impact would also be considered a high-severity deletion.`}</p>
        <p>{`For high-severity deletions, show a confirmation modal that explains what is being deleted and the consequences of the deletion, and have the user manually confirm the deletion by typing the name of the object they are deleting.`}</p>
        <p><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1632px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "56.61764705882353%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAALCAYAAAB/Ca1DAAAACXBIWXMAAAsSAAALEgHS3X78AAACG0lEQVQoz4WS32/SUBiGe7ub7l80yJgbXkxN3Aho1GRumiWOyUrpgCngxoSyIL+m2fTGaDJIXGBwoRsJcYzSlsKijLbnNJ+nnQnOOGzy5D1p8z7nO20p27VH2ZvTy+CwL4BzegmcU0tkvQgThD/TblsgPP4nZvf2jI/kfJbiuO1ELLoDoWBai0byOL75Du/vV3GpVMPFIsniRR4cfMWVyhGulIeUy99w9fAYf3hf0rbiexCJ5BLU8rMNnmPTsOLdQsxqEiIvc9AWZWhLErQEEURJBqEtgq7rcNV1WD1GwbUMsH6ep7zeTX6Ny4Dv+WvE+lOWUFF60O2dgSwr0Ol0ibQDzVMBTpoCfD85tTYS2hJZt0DpdqFaraNwKAdcYPtvIQ+xaAEkImiLklWSOwpomg4IITAM4xIIYWvCWm2EkLxHIlQsWUswpbI1Ye/sBxEjUFV9iKb/PvKVwosj9/sDqzwYaBbnA3XIuQo/yfM+SU3VADCCWuXokjDJBd6YQp1MaBChgTA2yMYjkVQwxHrTUO57jC/xtB6O7UKASSapFW88vR4ugPmFQ8EMvFjPQT7/CXYKn6EwgmyOkPkIBX4XEhtvYePVHgTYVNr8bVgilQl1lk01yM0Gmfa/rJr4Eg0fwzcYhq+Tnuxnkixln3SN2Rxz49cdLnpiyk1POu/Rs+4n9Jzn6UjuugmuRXr2zkN65tYD0vWM33B6xn4B7LudBukpcjgAAAAASUVORK5CYII=')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<img parentName="span" {...{
              "className": "gatsby-resp-image-image",
              "alt": "modal with an input and a destructive button to confirm deletion",
              "title": "modal with an input and a destructive button to confirm deletion",
              "src": "/static/1fccf8f53ab3abcd5a363750db5badfe/1d499/delete-high-severity.png",
              "srcSet": ["/static/1fccf8f53ab3abcd5a363750db5badfe/e7c18/delete-high-severity.png 408w", "/static/1fccf8f53ab3abcd5a363750db5badfe/b4098/delete-high-severity.png 816w", "/static/1fccf8f53ab3abcd5a363750db5badfe/1d499/delete-high-severity.png 1632w", "/static/1fccf8f53ab3abcd5a363750db5badfe/2ef06/delete-high-severity.png 2448w", "/static/1fccf8f53ab3abcd5a363750db5badfe/d9ed5/delete-high-severity.png 2880w"],
              "sizes": "(max-width: 1632px) 100vw, 1632px",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              },
              "loading": "lazy"
            }}></img>{`
    `}</span></p>
        <Box marginBottom="space80" mdxType="Box">
  <Disclosure variant="contained" mdxType="Disclosure">
    <DisclosureHeading as="h5" variant="heading50" mdxType="DisclosureHeading">
      Show live example
    </DisclosureHeading>
    <DisclosureContent mdxType="DisclosureContent">Coming soon!</DisclosureContent>
  </Disclosure>
        </Box>
        <h2>{`Post-deletion`}</h2>
        <p>{`After the user has deleted the object, navigate them to the index page, where they can see a list of all remaining objects, and show a success `}<a parentName="p" {...{
            "href": "../components/toast"
          }}>{`Toast`}</a>{` informing them that the object has successfully been deleted. If it is possible to undo the deletion, give the user the option to do so, and tell them how long they have to undo the deletion if it is time-bound.`}</p>
        <p>{`If the delete action fails, keep the modal open and display an error `}<a parentName="p" {...{
            "href": "../components/toast"
          }}>{`Toast`}</a>{` that explains what went wrong and how to try again.`}</p>
        <p>{`For more information, check out our `}<a parentName="p" {...{
            "href": "/patterns/notifications"
          }}>{`Notifications and Feedback patterns`}</a>{`.`}</p>
        <h2>{`Starter kits`}</h2>
        <h3>{`CodeSandbox`}</h3>
        <p>{`Coming soon`}</p>
        <h3>{`Figma`}</h3>
        <p>{`Coming soon`}</p>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      